import { Formik, useField, useFormikContext } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from 'components/Wizards/BlogPostWizard/BlogPostWizard.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { AI, ArrowRight } from 'almanac-ui/icons'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { useRef } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { Field } from 'components/Wizards/Field/Field'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { OutlineLoadingState } from 'components/Wizards/OutlineLoadingState/OutlineLoadingState'
import { OutlineEditor } from 'components/Wizards/BlogPostWizard/OutlineEditor/OutlineEditor'
import { OutlineEditorHandle } from 'components/Wizards/BlogPostWizard/OutlineEditor/Types'
import { useOutline } from 'components/Wizards/MultiStepBlogPostWizard/OutlineProvider'
import { Values } from './Types'
import { useGenerateIntro } from 'components/Wizards/MultiStepBlogPostWizard/useGenerateIntro'
import { useGenerateOutline } from 'components/Wizards/MultiStepBlogPostWizard/useGenerateOutline'
import { useLinks } from './LinksProvider'

interface Props extends Pick<StepProps, 'onBack'> {}

interface FormValues extends Values {
  title: string
  introduction: string
}

const SelectOutline = ({ onBack }: Props) => {
  const { values, submitForm, initialValues } = useFormikContext<FormValues>()
  const { abortGenerateOutline, abortGenerateIntro, isLoadingIntro, isLoadingOutline } = useOutline()
  const [{ value: title }, , { setValue: setTitle }] = useField<string>('title')
  const [{ value: introduction }, , { setValue: setIntroduction }] = useField<string>('introduction')
  const [{ value: outline }, , { setValue: setOutline }] = useField<string>('outline')

  const { isMobile, isTabletPortrait } = useResponsiveDesign()
  const outlineEditor = useRef<OutlineEditorHandle | null>(null)

  const generateIntro = useGenerateIntro({ onCancel: onBack })

  const generateHeaders = useGenerateOutline({ onCancel: onBack })

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields className={Styles.fieldsWrapper}>
            <Heading level={4} id="wizard-header">
              Edit your outline as desired
            </Heading>
            <Field>
              <div className={Styles.inputField}>
                <div className={Styles.inputLabel}>Title</div>
                <Input
                  isTextarea
                  scrollable={false}
                  className={Styles.boldInput}
                  name="title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value)
                  }}
                />
              </div>

              <div className={Styles.inputField}>
                <div className={Styles.inputLabel}>Introduction</div>
                {isLoadingIntro && <TextFieldLoadingState lines={3} variant="secondary" />}
                {!isLoadingIntro && (
                  <>
                    <Input
                      isTextarea
                      scrollable={false}
                      rows={4}
                      name="introduction"
                      value={introduction}
                      onChange={(e) => setIntroduction(e.target.value)}
                    />
                    <div className={Styles.inputActions}>
                      <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateIntro(values)}>
                        Regenerate Intro
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Field>

            <Field
              label="This is your blog's outline, with H2s and H3s for paragraph headers."
              description={
                <>
                  Press <kbd>enter</kbd> to add new H2s and H3s and <kbd>tab</kbd> <kbd>shift + tab</kbd> to indent /
                  unindent lines.
                </>
              }
            >
              {isLoadingOutline && <OutlineLoadingState />}
              {!isLoadingOutline && (
                <>
                  <OutlineEditor
                    key={initialValues.outline}
                    ref={outlineEditor}
                    aria-label="Outline"
                    className={Styles.outlineEditor}
                    initialHtmlString={outline}
                    onChange={setOutline}
                  />

                  <div className={Styles.inputActions}>
                    <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateHeaders(values)}>
                      Regenerate Headers
                    </Button>
                  </div>
                </>
              )}
            </Field>
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>
      <Form.Actions isSticky>
        {!isTabletPortrait && (
          <Form.PreviousButton
            onPress={() => {
              abortGenerateIntro()
              abortGenerateOutline()
              onBack?.()
            }}
          >
            Previous Step
          </Form.PreviousButton>
        )}

        <Form.PrimaryActions>
          <Button
            size="lg"
            endIcon={ArrowRight}
            onPress={submitForm}
            isDisabled={isLoadingOutline || isLoadingIntro}
            fullWidth={isMobile}
          >
            {'Select Links'}
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}

const SelectOutlineContainer = multiStep(({ schema, onSubmit, onBack, initialValues }: StepProps) => {
  const { generateLinks, setGeneratedLinks } = useLinks()

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values) => {
        setGeneratedLinks([])
        generateLinks(values)
        return onSubmit(values)
      }}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <SelectOutline onBack={onBack} />
    </Formik>
  )
})

export { SelectOutlineContainer as SelectOutline }
